import { useState, useCallback, useRef, useEffect } from 'react';
import { CalendarData } from '../types/calendar';

const isNoKnowledgeHitMessage = (message?: string): boolean => {
  const normalized = (message || '').toLowerCase();
  return (
    normalized.includes("don't have any relevant information for that query") ||
    normalized.includes("no relevant information was found in the knowledge base")
  );
};

interface StreamingEvent {
  type: 'metadata' | 'content' | 'complete' | 'error' | 'conversation' | 'conversation_not_found' | 'stream_complete' | 'reasoning';
  chat_id?: string;
  query_id?: string;
  content?: string;
  message?: string;
  sources?: Array<{
    document: string;
    similarity: number;
    chunk_id: string;
  }>;
  showCalendar?: boolean;
  calendarData?: CalendarData;
  performance?: {
    embedding_time?: number;
    database_time?: number;
    ai_time?: number;
    total_time?: number;
    api_calls?: number;
  };
  messages?: Array<{
    role: string;
    content: string;
    timestamp?: string;
  }>;
  debugReasoning?: boolean;
  reasoningSummary?: string;
}

interface StreamingResponse {
  message: string;
  isStreaming: boolean;
  sources: Array<{
    document: string;
    similarity: number;
    chunk_id: string;
  }>;
  showCalendar: boolean;
  calendarData?: CalendarData;
  performance?: {
    embedding_time?: number;
    database_time?: number;
    ai_time?: number;
    total_time?: number;
    api_calls?: number;
  };
  chatId?: string;
  error?: string;
  debugReasoning?: boolean;
  reasoningSummary?: string;
}

export const useWebSocketQuery = (useLocalEndpoint = false, enableWebSocket = true) => {
  const [response, setResponse] = useState<StreamingResponse>({
    message: '',
    isStreaming: false,
    sources: [],
    showCalendar: false,
    calendarData: undefined,
  });
  
  const wsRef = useRef<WebSocket | null>(null);
  const messageUpdateCallbackRef = useRef<((message: string, showCalendar: boolean) => void) | null>(null);
  const streamingStartTimeRef = useRef<number | null>(null);
  const firstContentTimeRef = useRef<number | null>(null);
  const lastContentTimeRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentQueryIdRef = useRef<string | null>(null);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');

  // WebSocket URL - based on configuration parameter
  const getWebSocketUrl = useCallback(() => {
    // Check for environment variable override (highest priority)
    if (process.env.REACT_APP_WS_URL) {
      return process.env.REACT_APP_WS_URL;
    }

    if (useLocalEndpoint) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const isSecure = window.location.protocol === 'https:';
      const isNgrok = host.includes('ngrok');
      const useProxy = process.env.REACT_APP_USE_PROXY === 'true';

      // For HTTPS (ngrok) or explicit proxy mode: use relative /ws path through proxy
      // This allows single-port architecture where proxy routes /ws to backend
      if (isSecure || isNgrok || useProxy) {
        return `${protocol}//${host}/ws`;
      }

      // Direct local development: connect directly to WebSocket server.
      // Use page hostname so LAN devices (e.g. iPhone) target the dev machine, not phone localhost.
      const wsHost = window.location.hostname || 'localhost';
      return `${protocol}//${wsHost}:8765`;
    } else {
      // Cloud WebSocket API Gateway
      return 'wss://23e8rrwx6i.execute-api.eu-central-1.amazonaws.com/prod';
    }
  }, [useLocalEndpoint]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    const wsUrl = getWebSocketUrl();
    console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);
    setConnectionState('connecting');

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ WebSocket connected');
      setConnectionState('connected');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onclose = (event) => {
      console.log('🔌 WebSocket disconnected:', event.code, event.reason);
      setConnectionState('disconnected');
      
      // Auto-reconnect after a delay (unless it was intentional)
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('🔄 Attempting to reconnect...');
          connect();
        }, 3000);
      }
    };

    ws.onerror = (errorEvent) => {
      const target = errorEvent.target as WebSocket | null;
      const readyState = target?.readyState;
      const stateLabel = readyState === WebSocket.CONNECTING
        ? 'CONNECTING'
        : readyState === WebSocket.OPEN
        ? 'OPEN'
        : readyState === WebSocket.CLOSING
        ? 'CLOSING'
        : readyState === WebSocket.CLOSED
        ? 'CLOSED'
        : 'UNKNOWN';
      console.error('❌ WebSocket error:', {
        type: errorEvent.type,
        isTrusted: (errorEvent as Event).isTrusted,
        url: target?.url || wsUrl,
        readyState,
        readyStateLabel: stateLabel,
      });
      setConnectionState('error');
    };

    ws.onmessage = (event) => {
      try {
        const streamingEvent: StreamingEvent = JSON.parse(event.data);
        // Check if this event is for the current query (ignore stale responses)
        if (streamingEvent.query_id && streamingEvent.query_id !== currentQueryIdRef.current) {
          return;
        }

        setResponse(prev => {
          switch (streamingEvent.type) {
            case 'metadata':
              return {
                ...prev,
                sources: streamingEvent.sources || [],
                showCalendar: streamingEvent.showCalendar || false,
                calendarData: streamingEvent.calendarData,
                chatId: streamingEvent.chat_id,
                performance: streamingEvent.performance ? { ...prev.performance, ...streamingEvent.performance } : prev.performance,
                debugReasoning: streamingEvent.debugReasoning,
                reasoningSummary: streamingEvent.reasoningSummary
              };

            case 'reasoning':
              return {
                ...prev,
                reasoningSummary: streamingEvent.reasoningSummary
              };

            case 'content':
              if (streamingEvent.content) {
                // Track content timing
                if (!firstContentTimeRef.current) {
                  firstContentTimeRef.current = Date.now();
                }
                lastContentTimeRef.current = Date.now();

                const newMessage = prev.message + streamingEvent.content;
                const contentUpdate = {
                  ...prev,
                  message: newMessage
                };

                // Call the callback for real-time UI updates
                if (messageUpdateCallbackRef.current) {
                  messageUpdateCallbackRef.current(newMessage, contentUpdate.showCalendar);
                }

                return contentUpdate;
              }
              return prev;

            case 'complete':
              if (streamingStartTimeRef.current) {
                const totalTime = Date.now() - streamingStartTimeRef.current;
                console.log(`Streaming complete: ${prev.message.length} chars in ${totalTime}ms`);
              }

              return {
                ...prev,
                isStreaming: false,
                performance: streamingEvent.performance ? { ...prev.performance, ...streamingEvent.performance } : prev.performance
              };

            case 'stream_complete':
              return {
                ...prev,
                isStreaming: false
              };

            case 'error':
              if (isNoKnowledgeHitMessage(streamingEvent.message)) {
                const fallbackMessage = streamingEvent.message || "I don't have any relevant information for that query.";
                if (messageUpdateCallbackRef.current) {
                  messageUpdateCallbackRef.current(fallbackMessage, prev.showCalendar);
                }
                return {
                  ...prev,
                  isStreaming: false,
                  error: undefined,
                  message: fallbackMessage
                };
              }
              console.warn('WebSocket response error event:', streamingEvent.message);
              // Ensure UI placeholder message is updated on errors
              if (messageUpdateCallbackRef.current) {
                const errorMessage = streamingEvent.message || 'An error occurred';
                messageUpdateCallbackRef.current(errorMessage, prev.showCalendar);
              }
              return {
                ...prev,
                isStreaming: false,
                error: streamingEvent.message || 'Unknown error',
                message: streamingEvent.message || 'An error occurred'
              };

            case 'conversation':
              return prev;

            case 'conversation_not_found':
              console.log('Conversation not found:', streamingEvent.chat_id);
              return prev;

            default:
              return prev;
          }
        });
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error, event.data);
      }
    };
  }, [getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  const sendQuery = useCallback(async (
    message: string,
    chatId?: string,
    conversationHistory?: Array<{role: string, content: string}>,
    workspaceSlug?: string,
    agentSlug?: string,
    avatarProfileSlug?: string,
  ) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.log('🔌 WebSocket not connected, connecting...');
      connect();
      
      // Wait for connection
      return new Promise<void>((resolve, reject) => {
        const checkConnection = () => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            sendQueryInternal(message, chatId, conversationHistory, workspaceSlug, agentSlug, avatarProfileSlug);
            resolve();
          } else if (wsRef.current?.readyState === WebSocket.CLOSED || wsRef.current?.readyState === WebSocket.CLOSING) {
            reject(new Error('Failed to connect to WebSocket'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }
    
    sendQueryInternal(message, chatId, conversationHistory, workspaceSlug, agentSlug, avatarProfileSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  const sendQueryInternal = useCallback((
    message: string,
    chatId?: string,
    conversationHistory?: Array<{role: string, content: string}>,
    workspaceSlug?: string,
    agentSlug?: string,
    avatarProfileSlug?: string,
  ) => {
    // Generate a unique query ID for this request
    const queryId = Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9);
    currentQueryIdRef.current = queryId;
    
    // Reset state and timing
    streamingStartTimeRef.current = Date.now();
    firstContentTimeRef.current = null;
    lastContentTimeRef.current = null;
    
    setResponse({
      message: '',
      isStreaming: true,
      sources: [],
      showCalendar: false,
      calendarData: undefined,
      chatId,
    });

    console.log(`🚀 Sending WebSocket query at ${new Date().toISOString()}`);
    console.log(`📝 Message: ${message}`);

    const adminToken = sessionStorage.getItem('admin_token');
    const queryMessage = {
      action: 'query',
      message,
      chat_id: chatId || null,
      conversation_history: conversationHistory || [],
      workspace_slug: workspaceSlug || null,
      agent_slug: agentSlug || null,
      avatar_profile_slug: avatarProfileSlug || null,
      query_id: queryId,
      ...(adminToken ? { auth_token: adminToken } : {}),
    };

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(queryMessage));
    } else {
      console.error('❌ WebSocket not ready to send message');
      setResponse(prev => ({
        ...prev,
        isStreaming: false,
        error: 'WebSocket connection not available',
        message: 'Sorry, I encountered a connection error. Please try again.',
      }));
    }
  }, []);

  const getConversation = useCallback((chatId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const adminToken = sessionStorage.getItem('admin_token');
      const conversationMessage = {
        action: 'get_conversation',
        chat_id: chatId,
        ...(adminToken ? { auth_token: adminToken } : {}),
      };
      wsRef.current.send(JSON.stringify(conversationMessage));
    } else {
      console.error('❌ WebSocket not ready to get conversation');
    }
  }, []);

  const registerMessageUpdateCallback = useCallback((callback: (message: string, showCalendar: boolean) => void) => {
    console.log(`🔗 Setting WebSocket message update callback - callback exists:`, !!callback);
    messageUpdateCallbackRef.current = callback;
  }, []);

  const clearResponse = useCallback(() => {
    console.log('🧹 Clearing WebSocket response state for new conversation');

    // Clear current query ID to ignore any future responses from previous queries
    currentQueryIdRef.current = null;

    const newResponse = {
      message: '',
      isStreaming: false,
      sources: [],
      showCalendar: false,
      calendarData: undefined,
      chatId: undefined,
      error: undefined,
      debugReasoning: undefined,
      reasoningSummary: undefined,
    };
    console.log('🧹 Setting response to:', newResponse);
    setResponse(newResponse);
  }, []);

  // Clean up on unmount — connection is established lazily on first sendQuery
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [connect, disconnect, enableWebSocket]);

  return {
    response,
    sendQuery,
    getConversation,
    isStreaming: response.isStreaming,
    connectionState,
    connect,
    disconnect,
    registerMessageUpdateCallback,
    clearResponse,
  };
};
